import Phaser from 'phaser';
import { sortedAtlasFrames } from '../../atlas-anims';
import { GEODE_DEFS } from '../../geode';

export const GEODE_BREAK_TIMING = {
  postBreakSettleMs: 350,
  fadeOutMs: 500,
  postFadeMs: 100,
  respawnDropMs: 550,
};

export function geodeBreakTextureKey(geodeKey: string) {
  return `geode-break-${geodeKey.replace(/[^a-z0-9]+/gi, '_')}`;
}

export function geodeBreakAnimKey(geodeKey: string) {
  return `geode-break-anim-${geodeKey.replace(/[^a-z0-9]+/gi, '_')}`;
}

export function queueGeodeBreakLoad(scene: Phaser.Scene, geodeKey: string) {
  const def = GEODE_DEFS[geodeKey];
  if (!def?.breakSheet || !def.jsonPath) return;

  const textureKey = geodeBreakTextureKey(geodeKey);
  if (scene.textures.exists(textureKey)) return;

  scene.load.atlas(
    textureKey,
    `${def.path}${def.breakSheet}`,
    `${def.path}${def.jsonPath}`,
  );
}

export function prepareGeodeBreakAnim(scene: Phaser.Scene, geodeKey: string) {
  const def = GEODE_DEFS[geodeKey];
  if (!def) return false;

  const textureKey = geodeBreakTextureKey(geodeKey);
  const animKey = geodeBreakAnimKey(geodeKey);
  if (!scene.textures.exists(textureKey)) return false;
  if (scene.anims.exists(animKey)) return true;

  const atlasFrames = sortedAtlasFrames(scene, textureKey);
  const frameCount = atlasFrames.length || def.breakFrames;
  const frameRate = frameCount / def.breakDur;

  if (atlasFrames.length) {
    scene.anims.create({
      key: animKey,
      frames: atlasFrames.map((frame) => ({ key: textureKey, frame })),
      frameRate,
      repeat: 0,
    });
    return true;
  }

  scene.anims.create({
    key: animKey,
    frames: scene.anims.generateFrameNumbers(textureKey, {
      start: 0,
      end: Math.max(0, def.breakFrames - 1),
    }),
    frameRate,
    repeat: 0,
  });
  return true;
}

export function playGeodeBreakVfx(
  scene: Phaser.Scene,
  { x, y, display }: { x: number; y: number; display: number },
  geodeKey: string,
  { crackSprite }: { crackSprite?: Phaser.GameObjects.Image } = {},
) {
  const textureKey = geodeBreakTextureKey(geodeKey);
  const animKey = geodeBreakAnimKey(geodeKey);
  if (!scene.textures.exists(textureKey) || !scene.anims.exists(animKey)) {
    return Promise.resolve();
  }

  const atlasFrames = sortedAtlasFrames(scene, textureKey);
  const firstFrame = atlasFrames.length ? atlasFrames[0] : 0;

  const breakSprite = scene.add
    .sprite(x, y, textureKey, firstFrame)
    .setDepth(12)
    .setOrigin(0.5, 0.5)
    .setDisplaySize(display, display);

  if (crackSprite) {
    crackSprite.setVisible(false);
  }

  return new Promise<void>((resolve) => {
    breakSprite.once('animationcomplete', () => {
      breakSprite.destroy();
      resolve();
    });
    breakSprite.play(animKey);
  });
}
